import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const COVER_TYPES = ["image/jpeg", "image/png", "image/webp"];
const COVER_MAX_MB = 8;

/** Drag-and-drop / file-picker cover upload → Emergent object storage.
 *  Returns an ABSOLUTE backend URL so covers render on the CRM preview
 *  AND the public website regardless of origin. */
function PdfUploader({ onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name || "")) {
      toast.error("Only PDF files are allowed.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("PDF too large — max 15 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "landing-pdf");
      const { data } = await api.post("/admin/sbt-site/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = data.public_url && data.public_url.startsWith("http")
        ? data.public_url
        : `${base}${data.url}`;
      onUploaded(url);
      toast.success("PDF uploaded & hosted — URL filled in");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed — try again.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div
      data-testid="pdf-upload-dropzone"
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
      className={`mt-1.5 rounded border border-dashed px-3 py-2 text-[11px] cursor-pointer select-none transition-colors ${
        dragOver ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-500 hover:border-slate-400"
      }`}
    >
      {busy ? "Uploading PDF…" : "…or drag & drop / click to upload a PDF to host (≤ 15 MB)"}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        data-testid="pdf-upload-input"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
}

function CoverUploader({ onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!COVER_TYPES.includes(file.type)) {
      toast.error("Only JPG, PNG or WebP images are allowed.");
      return;
    }
    if (file.size > COVER_MAX_MB * 1024 * 1024) {
      toast.error(`Image too large — max ${COVER_MAX_MB} MB.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "landing-cover");
      const { data } = await api.post("/admin/sbt-site/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const url = data.public_url && data.public_url.startsWith("http")
        ? data.public_url
        : `${base}${data.url}`;
      onUploaded(url);
      toast.success("Cover image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed — try again.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div
      data-testid="cover-upload-dropzone"
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
      className={`mt-1.5 rounded border border-dashed px-3 py-2 text-[11px] cursor-pointer select-none transition-colors ${
        dragOver ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-300 text-slate-500 hover:border-slate-400"
      }`}
    >
      {busy ? "Uploading…" : "…or drag & drop / click to upload a cover (JPG · PNG · WebP, ≤ 8 MB)"}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid="cover-upload-input"
        onChange={(e) => upload(e.target.files?.[0])}
      />
    </div>
  );
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, Eye, FileText, Languages, Loader2, ExternalLink } from "lucide-react";
import { RichLinkEditor, RichText } from "@/components/RichLinkEditor";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LandingPageBuilder from "@/pages/LandingPageBuilder";

/**
 * AdminSbtLandingPages — Whitepaper / Content-library landing-page editor.
 *
 * Layout: two-column split.
 *   • LEFT  — structured form (title, cover, abstract, highlights, legal,
 *             form-field toggles, CTA, PDF, category, language).
 *   • RIGHT — live preview mimicking the sbtresearch.com/company/detail/…
 *             landing page a visitor sees.
 *
 * Features:
 *   • List of all landing pages with publish/unpublish toggle
 *   • AI Translate button — asks Claude (via Emergent LLM key) to translate
 *     every text field into a target language; result is copied into the
 *     form and can be saved as a new language version of the same slug.
 *   • Publish flips status; slug + language forms the public URL.
 */

const CATEGORIES = [
  { value: "whitepaper", label: "Whitepaper" },
  { value: "ebook",       label: "eBook" },
  { value: "case-study",  label: "Case Study" },
  { value: "blog",        label: "Blog" },
  { value: "webinar",     label: "Webinar" },
  { value: "video",       label: "Video" },
  { value: "buyers-guide",label: "Buyer's Guide" },
];

// Top languages we let admin translate into. Any 2-letter ISO code works
// though — Claude handles the rest.
const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish (Español)" },
  { value: "fr", label: "French (Français)" },
  { value: "de", label: "German (Deutsch)" },
  { value: "it", label: "Italian (Italiano)" },
  { value: "pt", label: "Portuguese (Português)" },
  { value: "nl", label: "Dutch (Nederlands)" },
  { value: "sv", label: "Swedish (Svenska)" },
  { value: "pl", label: "Polish (Polski)" },
  { value: "ru", label: "Russian (Русский)" },
  { value: "ar", label: "Arabic (العربية)" },
  { value: "he", label: "Hebrew (עברית)" },
  { value: "hi", label: "Hindi (हिन्दी)" },
  { value: "ja", label: "Japanese (日本語)" },
  { value: "ko", label: "Korean (한국어)" },
  { value: "zh", label: "Chinese (中文)" },
  { value: "id", label: "Indonesian (Bahasa)" },
  { value: "tr", label: "Turkish (Türkçe)" },
  { value: "vi", label: "Vietnamese (Tiếng Việt)" },
  { value: "th", label: "Thai (ภาษาไทย)" },
];

const DEFAULT_FIELDS = {
  full_name: true, email: true, company: true, job_title: true,
  phone: false, country: true, marketing_opt: true, privacy_opt: true,
};

const emptyPage = () => ({
  id: null,
  slug: "",
  language: "en",
  category: "whitepaper",
  title: "",
  cover_url: "",
  abstract: "",
  about_summary: "",
  learn_bullets: [],
  highlights: [],
  legal_blurb: "",
  form_fields: { ...DEFAULT_FIELDS },
  form_fields_v2: [],
  consent_config: {},
  cta_label: "Download Now",
  pdf_url: "",
  status: "draft",
  layout_density: "normal",
  blocks: [],
});

const FIELD_TYPES = [
  { value: "text",     label: "Text (single line)" },
  { value: "email",    label: "Email" },
  { value: "tel",      label: "Phone" },
  { value: "number",   label: "Number" },
  { value: "url",      label: "URL" },
  { value: "textarea", label: "Long text (textarea)" },
  { value: "select",   label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
];

const uidField = () => `f_${Math.random().toString(36).slice(2, 8)}`;

export default function AdminSbtLandingPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [draft, setDraft] = useState(emptyPage());
  const [translateTarget, setTranslateTarget] = useState("es");

  // ── Filters + pagination ────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState("all");     // all | draft | published
  const [industryFilter, setIndustryFilter] = useState("all"); // "all" | industry name
  // Canonical industry list — same source as the Media Kit "Switch Industry"
  // dropdown (/sbt-site/trending-industries).
  const [canonicalIndustries, setCanonicalIndustries] = useState([]);

  useEffect(() => {
    api.get("/sbt-site/trending-industries")
      .then(({ data }) => setCanonicalIndustries(data?.industries || []))
      .catch(() => setCanonicalIndustries([]));
  }, []);
  const [search, setSearch] = useState("");
  const [pageIdx, setPageIdx] = useState(0);                    // 0-based
  const PAGE_SIZE = 20;
  const industryOf = (p) => ((p && p.source_meta && p.source_meta.industry) || "Uncategorized");

  // ── Bulk selection ──────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPublishing, setBulkPublishing] = useState(false);

  const publicOrigin = useMemo(() => {
    // Astro pages served under the same origin at /sbt-site/
    if (typeof window === "undefined") return "";
    return window.location.origin;
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sbt-site/landing-pages");
      setPages(r.data.pages || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load landing pages");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPageIdx(0); setSelectedIds(new Set()); }, [statusFilter, industryFilter, search]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Bulk publish only makes sense for drafts on the current page.
  const bulkPublish = async (destination) => {
    const draftsSelected = Array.from(selectedIds).filter((id) => {
      const p = pages.find((x) => x.id === id);
      return p && p.status === "draft";
    });
    if (draftsSelected.length === 0) {
      toast.error("Select at least one draft to publish");
      return;
    }
    if (!confirm(`Publish ${draftsSelected.length} draft${draftsSelected.length === 1 ? "" : "s"} to ${destination === "market_plus" ? "Content Library" : "Resources"}?`)) return;
    setBulkPublishing(true);
    try {
      const { data } = await api.post("/admin/sbt-site/landing-pages/bulk-publish", {
        page_ids: draftsSelected,
        destination,
      });
      toast.success(`Published ${data.published} · already-published ${data.already_published} · missing ${data.missing}`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk publish failed");
    } finally {
      setBulkPublishing(false);
    }
  };

  // Filter → search → paginate. All client-side; the admin endpoint
  // returns the full landing-page set which is only a few hundred rows.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pages.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (industryFilter !== "all" && industryOf(p) !== industryFilter) return false;
      if (!q) return true;
      return (
        (p.title || "").toLowerCase().includes(q) ||
        (p.slug || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q) ||
        industryOf(p).toLowerCase().includes(q)
      );
    });
  }, [pages, statusFilter, industryFilter, search]);

  // Full canonical list (Media Kit "Switch Industry" order) + any extra
  // values found on existing drafts (legacy topics, "Uncategorized").
  const industries = useMemo(() => {
    const inDrafts = new Set(pages.map(industryOf));
    const canonical = canonicalIndustries.filter(Boolean);
    const canonicalSet = new Set(canonical);
    const extras = Array.from(inDrafts).filter((i) => !canonicalSet.has(i)).sort((a, b) => a.localeCompare(b));
    return [...canonical, ...extras];
  }, [pages, canonicalIndustries]);

  const counts = useMemo(() => ({
    all: pages.length,
    draft: pages.filter((p) => p.status === "draft").length,
    published: pages.filter((p) => p.status === "published").length,
  }), [pages]);

  const industryCounts = useMemo(() => {
    const m = {};
    for (const p of pages) {
      const k = industryOf(p);
      m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [pages]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(pageIdx, totalPages - 1);
  const paged = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const openNew = () => { setDraft(emptyPage()); setOpenEditor(true); };
  const openEdit = async (p) => {
    // The list endpoint omits heavy fields (blocks) — fetch the full doc.
    let full = p;
    try {
      const r = await api.get(`/admin/sbt-site/landing-pages/${p.id}`);
      if (r.data && r.data.id) full = r.data;
    } catch (e) { /* fall back to the row we already have */ }
    setDraft({
      ...emptyPage(),
      ...full,
      form_fields: { ...DEFAULT_FIELDS, ...(full.form_fields || {}) },
      form_fields_v2: Array.isArray(full.form_fields_v2) ? [...full.form_fields_v2] : [],
      consent_config: { ...(full.consent_config || {}) },
      highlights: Array.isArray(full.highlights) ? [...full.highlights] : [],
      learn_bullets: Array.isArray(full.learn_bullets) ? [...full.learn_bullets] : [],
      about_summary: full.about_summary || "",
      layout_density: full.layout_density || "normal",
      blocks: Array.isArray(full.blocks) ? [...full.blocks] : [],
    });
    setOpenEditor(true);
  };

  const setF = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const setFormField = (k) => (v) =>
    setDraft((d) => ({ ...d, form_fields: { ...d.form_fields, [k]: v } }));
  const setConsent = (k) => (v) =>
    setDraft((d) => ({ ...d, consent_config: { ...(d.consent_config || {}), [k]: v } }));

  const setHighlightAt = (i, v) => setDraft((d) => {
    const hl = [...d.highlights]; hl[i] = v; return { ...d, highlights: hl };
  });
  const addHighlight = () =>
    setDraft((d) => ({ ...d, highlights: [...d.highlights, ""] }));
  const removeHighlightAt = (i) =>
    setDraft((d) => ({ ...d, highlights: d.highlights.filter((_, idx) => idx !== i) }));

  // "In this [type], you'll learn:" bullet editor helpers
  const setLearnAt = (i, v) => setDraft((d) => {
    const lb = [...(d.learn_bullets || [])]; lb[i] = v; return { ...d, learn_bullets: lb };
  });
  const addLearn = () =>
    setDraft((d) => ({ ...d, learn_bullets: [...(d.learn_bullets || []), ""] }));
  const removeLearnAt = (i) =>
    setDraft((d) => ({ ...d, learn_bullets: (d.learn_bullets || []).filter((_, idx) => idx !== i) }));

  const save = async ({ publish = false, destination = null } = {}) => {
    if (!draft.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        slug: (draft.slug || draft.title).toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, ""),
        status: publish ? "published" : (draft.status || "draft"),
        highlights: draft.highlights.map((h) => (h || "").trim()).filter(Boolean),
        learn_bullets: (draft.learn_bullets || []).map((h) => (h || "").trim()).filter(Boolean),
        about_summary: (draft.about_summary || draft.abstract || "").trim(),
        layout_density: ["compact", "normal", "spacious"].includes(draft.layout_density) ? draft.layout_density : "normal",
        blocks: Array.isArray(draft.blocks) ? draft.blocks : [],
        form_fields_v2: Array.isArray(draft.form_fields_v2) ? draft.form_fields_v2 : [],
        consent_config: draft.consent_config || {},
      };
      if (publish && destination) {
        payload.publish_destination = destination;
      }
      let saved;
      if (draft.id) {
        const r = await api.put(`/admin/sbt-site/landing-pages/${draft.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/admin/sbt-site/landing-pages", payload);
        saved = r.data;
      }
      // The PUT/POST above persists publish_destination directly — do NOT
      // call the /publish toggle endpoint here (it would flip the freshly
      // published page back to draft).
      setDraft(saved);
      const destLabel = destination === "market_plus" ? "Content Library" : "Resources";
      toast.success(publish ? `Published to ${destLabel}` : "Saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const togglePublish = async (page, destination = null) => {
    // Publish to a specific destination:
    //   • "resources"   → shows on marketing site's public Resources hub
    //   • "market_plus" → shows only in the gated Content Library
    // If `destination` is null and the page is already published,
    // this call unpublishes it.
    try {
      const url = destination
        ? `/admin/sbt-site/landing-pages/${page.id}/publish?destination=${encodeURIComponent(destination)}`
        : `/admin/sbt-site/landing-pages/${page.id}/publish`;
      const r = await api.post(url);
      const dest = r.data.publish_destination || destination;
      const destLabel = dest === "market_plus" ? "Content Library" : "Resources";
      toast.success(r.data.status === "published"
        ? `Published to ${destLabel}`
        : `Status → ${r.data.status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to toggle publish");
    }
  };

  const remove = async (page) => {
    if (!window.confirm(`Delete "${page.title}" (${page.language})?`)) return;
    try {
      await api.delete(`/admin/sbt-site/landing-pages/${page.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const translate = async () => {
    if (!draft.id) { toast.error("Save the page first, then translate."); return; }
    setTranslating(true);
    try {
      const r = await api.post(
        `/admin/sbt-site/landing-pages/${draft.id}/translate`,
        { target_language: translateTarget }
      );
      // API returns a new-language variant (no id); merge into draft so
      // admin can review & save it as a new row.
      setDraft({
        ...emptyPage(),
        ...draft,
        ...r.data,
        id: null,               // save-as-new
        status: "draft",
        language: translateTarget,
      });
      toast.success(`Translated to ${translateTarget}. Review then Save.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Translation failed");
    } finally { setTranslating(false); }
  };

  const previewUrl = (p, mode = "landing") => {
    // For unpublished drafts, append `preview_key=<page.id>` so the
    // Astro pages can fetch the draft via the admin-only bearer path.
    //   mode = "landing"  → /resources/detail/    (lead-form + about)
    //   mode = "document" → /resources/document/  (whitepaper article view)
    const path = mode === "document" ? "resources/document" : "resources/detail";
    const base = `${publicOrigin}/sbt-site/${path}/?slug=${encodeURIComponent(p.slug)}&lang=${p.language}`;
    return p.status === "published" ? base : `${base}&preview_key=${encodeURIComponent(p.id)}`;
  };

  return (
    <div className="space-y-4" data-testid="sbt-landing-pages-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Content Library — Landing Pages</h3>
          <p className="text-sm text-slate-500 mt-1">
            Build gated whitepaper / eBook / case-study landing pages. Published pages appear
            in the marketing site&apos;s Content Library and are indexed at
            <code className="mx-1 text-xs bg-slate-100 rounded px-1 py-0.5">/sbt-site/resources/detail/?slug=&hellip;</code>.
          </p>
        </div>
        <Button onClick={openNew} data-testid="landing-new-btn" className="rounded-sm">
          <Plus className="w-4 h-4 mr-2"/> New Landing Page
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="landing-loading">Loading…</div>
      ) : pages.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 p-10 text-center text-slate-500 text-sm" data-testid="landing-empty">
          No landing pages yet. Click <strong>New Landing Page</strong> to create your first whitepaper page.
        </div>
      ) : (
        <>
          {/* ── Filters + search ─────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2 border border-slate-200 bg-slate-50 rounded-sm px-3 py-2">
            <div className="inline-flex rounded-sm border border-slate-300 bg-white overflow-hidden" data-testid="landing-status-tabs">
              {[
                { k: "all",       label: "All",       n: counts.all       },
                { k: "draft",     label: "Drafts",    n: counts.draft     },
                { k: "published", label: "Published", n: counts.published },
              ].map(({ k, label, n }) => (
                <button
                  key={k}
                  onClick={() => setStatusFilter(k)}
                  className={`px-3 py-1 text-xs font-medium ${statusFilter === k ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                  data-testid={`landing-status-tab-${k}`}
                >
                  {label} <span className="ml-1 tabular-nums opacity-75">({n})</span>
                </button>
              ))}
            </div>
            <select
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              data-testid="landing-industry-filter"
              className="h-7 rounded-sm border border-slate-300 bg-white px-2 text-xs max-w-[240px]"
              title="Filter by the industry topic this draft was generated under"
            >
              <option value="all">All industries ({industries.length})</option>
              {industries.map((i) => (
                <option key={i} value={i}>{i}{industryCounts[i] ? ` (${industryCounts[i]})` : ""}</option>
              ))}
            </select>
            <input
              type="search"
              placeholder="Search title, slug, category…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ml-auto h-7 min-w-[220px] rounded-sm border border-slate-300 bg-white px-2 text-xs"
              data-testid="landing-search-input"
            />
            <div className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap">
              {filtered.length === pages.length
                ? `${pages.length} total`
                : `${filtered.length} of ${pages.length}`}
            </div>
          </div>

          {/* ── Bulk action bar (only visible when something is selected) ── */}
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-sm border border-cyan-300 bg-cyan-50 px-3 py-2 text-xs" data-testid="landing-bulk-bar">
              <span className="font-semibold text-cyan-900 tabular-nums">
                {selectedIds.size} selected
              </span>
              <span className="text-slate-600">
                ({Array.from(selectedIds).filter((id) => pages.find((p) => p.id === id)?.status === "draft").length} draft{Array.from(selectedIds).filter((id) => pages.find((p) => p.id === id)?.status === "draft").length === 1 ? "" : "s"} publishable)
              </span>
              <div className="ml-auto flex items-center gap-1">
                <Button
                  size="sm"
                  className="h-7 rounded-sm text-xs bg-cyan-700 hover:bg-cyan-800 text-white"
                  onClick={() => bulkPublish("resources")}
                  disabled={bulkPublishing}
                  data-testid="landing-bulk-publish-resources-btn"
                >
                  {bulkPublishing ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : null}
                  Publish → Resources
                </Button>
                <Button
                  size="sm"
                  className="h-7 rounded-sm text-xs bg-indigo-700 hover:bg-indigo-800 text-white"
                  onClick={() => bulkPublish("market_plus")}
                  disabled={bulkPublishing}
                  data-testid="landing-bulk-publish-cl-btn"
                >
                  Publish → Content Library
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-sm text-xs"
                  onClick={() => setSelectedIds(new Set())}
                  data-testid="landing-bulk-clear-btn"
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="rounded border border-dashed border-slate-300 p-10 text-center text-slate-500 text-sm" data-testid="landing-filtered-empty">
              No landing pages match this filter.
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="min-w-full text-sm" data-testid="landing-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium w-8">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 cursor-pointer accent-cyan-600"
                        title="Select all drafts on this page"
                        data-testid="landing-select-all-page"
                        checked={(() => {
                          const draftIds = paged.filter((p) => p.status === "draft").map((p) => p.id);
                          return draftIds.length > 0 && draftIds.every((id) => selectedIds.has(id));
                        })()}
                        onChange={(e) => {
                          const draftIds = paged.filter((p) => p.status === "draft").map((p) => p.id);
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) draftIds.forEach((id) => next.add(id));
                            else draftIds.forEach((id) => next.delete(id));
                            return next;
                          });
                        }}
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-medium">Title</th>
                    <th className="px-3 py-2 text-left font-medium">Slug</th>
                    <th className="px-3 py-2 text-left font-medium">Language</th>
                    <th className="px-3 py-2 text-left font-medium">Category</th>
                    <th className="px-3 py-2 text-left font-medium">Industry</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Destination</th>
                    <th className="px-3 py-2 text-left font-medium">Updated</th>
                    <th className="px-3 py-2 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p) => (
                    <tr key={p.id} className={`border-t hover:bg-slate-50 ${selectedIds.has(p.id) ? "bg-cyan-50/60" : ""}`} data-testid={`landing-row-${p.slug}-${p.language}`}>
                  <td className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 cursor-pointer accent-cyan-600 disabled:opacity-30"
                      disabled={p.status === "published"}
                      title={p.status === "published" ? "Already published" : "Select for bulk action"}
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      data-testid={`landing-select-${p.slug}-${p.language}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-900">{p.title}</td>
                  <td className="px-3 py-2 text-slate-600"><code className="text-xs">{p.slug}</code></td>
                  <td className="px-3 py-2 text-slate-600 uppercase text-xs">{p.language}</td>
                  <td className="px-3 py-2 text-slate-600">{p.category}</td>
                  <td className="px-3 py-2" data-testid={`landing-industry-${p.slug}-${p.language}`}>
                    <span className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200 whitespace-nowrap">
                      {industryOf(p)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${p.status === "published" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`landing-dest-${p.slug}-${p.language}`}>
                    {p.status === "published" ? (
                      <span className={`inline-flex items-center rounded px-2 py-0.5 font-semibold ${p.publish_destination === "market_plus" ? "bg-indigo-50 text-indigo-700 border border-indigo-200" : "bg-cyan-50 text-cyan-700 border border-cyan-200"}`}>
                        {p.publish_destination === "market_plus" ? "Content Library" : "Resources"}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{(p.updated_at || "").split("T")[0]}</td>
                  <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => window.open(previewUrl(p, "document"), "_blank")} data-testid={`landing-view-doc-${p.slug}-${p.language}`} title="Preview whitepaper / document (article view)">
                      <FileText className="w-4 h-4"/>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => window.open(previewUrl(p, "landing"), "_blank")} data-testid={`landing-view-${p.slug}-${p.language}`} title="Preview landing page (form + about)">
                      <Eye className="w-4 h-4"/>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`landing-edit-${p.slug}-${p.language}`} title="Edit">
                      <Pencil className="w-4 h-4"/>
                    </Button>
                    {p.status === "published" ? (
                      <Button size="sm" variant="ghost" onClick={() => togglePublish(p)} data-testid={`landing-unpublish-${p.slug}-${p.language}`} title="Unpublish (move back to draft)">
                        Unpublish
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => togglePublish(p, "market_plus")} data-testid={`landing-publish-cl-${p.slug}-${p.language}`} title="Publish to Content Library only (gated; not on public Resources hub)" className="text-indigo-700 hover:bg-indigo-50">
                          → CL
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => togglePublish(p, "resources")} data-testid={`landing-publish-res-${p.slug}-${p.language}`} title="Publish to public Resources hub" className="text-cyan-700 hover:bg-cyan-50">
                          → Res
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`landing-delete-${p.slug}-${p.language}`} title="Delete">
                      <Trash2 className="w-4 h-4 text-rose-600"/>
                    </Button>
                  </td>
                </tr>
              ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination controls ────────────────────────────── */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-xs text-slate-600 mt-2" data-testid="landing-pagination">
              <span className="tabular-nums">
                Showing <b>{currentPage * PAGE_SIZE + 1}</b>–<b>{Math.min((currentPage + 1) * PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b>
              </span>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 rounded-sm text-xs"
                  onClick={() => setPageIdx(0)}
                  disabled={currentPage === 0}
                  data-testid="landing-page-first"
                >« First</Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 rounded-sm text-xs"
                  onClick={() => setPageIdx((i) => Math.max(0, i - 1))}
                  disabled={currentPage === 0}
                  data-testid="landing-page-prev"
                >‹ Prev</Button>
                <span className="mx-2 tabular-nums">Page {currentPage + 1} / {totalPages}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 rounded-sm text-xs"
                  onClick={() => setPageIdx((i) => Math.min(totalPages - 1, i + 1))}
                  disabled={currentPage >= totalPages - 1}
                  data-testid="landing-page-next"
                >Next ›</Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 rounded-sm text-xs"
                  onClick={() => setPageIdx(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  data-testid="landing-page-last"
                >Last »</Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Editor Dialog ─────────────────────────────────── */}
      <Dialog open={openEditor} onOpenChange={setOpenEditor}>
        <DialogContent className="max-w-[1400px] w-[95vw] h-[92vh] p-0 gap-0 flex flex-col" data-testid="landing-editor-dialog">
          <DialogHeader className="px-6 py-4 border-b flex-row items-center justify-between">
            <DialogTitle className="text-base font-semibold">
              {draft.id ? "Edit Landing Page" : "New Landing Page"}
              {draft.id && <span className="ml-2 text-xs text-slate-400">{draft.slug} · {draft.language}</span>}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Select value={translateTarget} onValueChange={setTranslateTarget}>
                <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="translate-lang-select">
                  <SelectValue placeholder="Language"/>
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={translate} disabled={translating || !draft.id} data-testid="translate-btn">
                {translating ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Languages className="w-4 h-4 mr-2"/>}
                Translate
              </Button>
              <Button variant="outline" size="sm" onClick={() => save({ publish: false })} disabled={saving} data-testid="save-draft-btn">
                {saving ? "Saving…" : "Save Draft"}
              </Button>
              <Button size="sm" onClick={() => save({ publish: true, destination: "market_plus" })} disabled={saving} data-testid="publish-cl-btn" className="bg-indigo-600 hover:bg-indigo-700">
                {saving ? "…" : "Publish → Content Library"}
              </Button>
              <Button size="sm" onClick={() => save({ publish: true, destination: "resources" })} disabled={saving} data-testid="publish-res-btn" className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? "…" : "Publish → Resources"}
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 divide-x overflow-hidden">
            {/* Left — Editor (tabbed) */}
            <div className="overflow-hidden flex flex-col" data-testid="landing-editor-form">
              <Tabs defaultValue="structured" className="flex-1 min-h-0 flex flex-col">
                <TabsList className="mx-6 mt-4 self-start">
                  <TabsTrigger value="structured" data-testid="tab-structured">Structured</TabsTrigger>
                  <TabsTrigger value="builder" data-testid="tab-builder">Drag-Drop Builder</TabsTrigger>
                </TabsList>

                <TabsContent value="structured" className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5 m-0">
                  <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Slug</Label>
                  <Input value={draft.slug} onChange={(e) => setF("slug")(e.target.value)} placeholder="oracle-multicloud" data-testid="form-slug"/>
                </div>
                <div>
                  <Label className="text-xs">Language</Label>
                  <Select value={draft.language} onValueChange={setF("language")}>
                    <SelectTrigger data-testid="form-language"><SelectValue/></SelectTrigger>
                    <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={draft.category} onValueChange={setF("category")}>
                    <SelectTrigger data-testid="form-category"><SelectValue/></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs">Title (H1)</Label>
                <Textarea value={draft.title} onChange={(e) => setF("title")(e.target.value)} rows={2} placeholder="The Multicloud Imperative…" data-testid="form-title"/>
              </div>

              <div>
                <Label className="text-xs">Cover image URL</Label>
                <Input value={draft.cover_url} onChange={(e) => setF("cover_url")(e.target.value)} placeholder="https://…/cover.jpg" data-testid="form-cover"/>
                <CoverUploader onUploaded={(url) => setF("cover_url")(url)} />
              </div>

              <div>
                <Label className="text-xs">Abstract / Body <span className="text-slate-400">(blank line = new paragraph)</span></Label>
                <RichLinkEditor value={draft.abstract} onChange={setF("abstract")} rows={8} placeholder="Data fuels the modern enterprise…" dataTestId="form-abstract"/>
              </div>

              <div>
                <Label className="text-xs">
                  About the {String(draft.category || "guide").replace("-", " ").replace(/s$/, "")} <span className="text-slate-400">(shorter version — 1-3 sentences shown on landing page. Falls back to Abstract if blank.)</span>
                </Label>
                <RichLinkEditor
                  value={draft.about_summary || ""}
                  onChange={setF("about_summary")}
                  rows={3}
                  placeholder="A concise 250-char summary that appears on the landing page above the form…"
                  dataTestId="form-about-summary"
                />
                <div className="text-[10px] text-slate-400 text-right mt-0.5">{(draft.about_summary || "").replace(/<a\s[^>]*>|<\/a>/gi, "").length}/250 recommended</div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">
                    In this {String(draft.category || "guide").replace("-", " ").replace(/s$/, "")}, you&apos;ll learn: <span className="text-slate-400">(5-6 action-oriented bullets)</span>
                  </Label>
                  <Button size="sm" variant="outline" onClick={addLearn} data-testid="form-add-learn">
                    <Plus className="w-3.5 h-3.5 mr-1"/> Add
                  </Button>
                </div>
                <div className="space-y-1.5 mt-1.5">
                  {(draft.learn_bullets || []).length === 0 && (
                    <p className="text-xs text-slate-400">No bullets yet — click Add. This section is hidden on the landing page when empty.</p>
                  )}
                  {(draft.learn_bullets || []).map((h, i) => (
                    <div key={i} className="flex gap-1.5">
                      <Input value={h} onChange={(e) => setLearnAt(i, e.target.value)} placeholder="How to…" data-testid={`form-learn-${i}`}/>
                      <Button size="sm" variant="ghost" onClick={() => removeLearnAt(i)} title="Remove">
                        <Trash2 className="w-4 h-4 text-rose-600"/>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Layout density <span className="text-slate-400">(vertical spacing preset)</span></Label>
                <div className="mt-1.5 inline-flex rounded-lg border border-slate-300 overflow-hidden bg-white" data-testid="form-density-group">
                  {["compact", "normal", "spacious"].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setF("layout_density")(d)}
                      data-testid={`form-density-${d}`}
                      className={`px-3 py-1.5 text-xs font-semibold capitalize transition ${draft.layout_density === d ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Applied per landing page — controls the gap between Title → About → Learn sections.</p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Highlights <span className="text-slate-400">(bullet list)</span></Label>
                  <Button size="sm" variant="outline" onClick={addHighlight} data-testid="form-add-highlight">
                    <Plus className="w-3.5 h-3.5 mr-1"/> Add
                  </Button>
                </div>
                <div className="space-y-1.5 mt-1.5">
                  {draft.highlights.length === 0 && (
                    <p className="text-xs text-slate-400">No highlights yet — click Add.</p>
                  )}
                  {draft.highlights.map((h, i) => (
                    <div key={i} className="flex gap-1.5">
                      <Input value={h} onChange={(e) => setHighlightAt(i, e.target.value)} data-testid={`form-highlight-${i}`}/>
                      <Button size="sm" variant="ghost" onClick={() => removeHighlightAt(i)} title="Remove">
                        <Trash2 className="w-4 h-4 text-rose-600"/>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Legal / privacy blurb <span className="text-slate-400">(italicised in preview)</span></Label>
                <RichLinkEditor value={draft.legal_blurb} onChange={setF("legal_blurb")} rows={4} placeholder="This is cohosted by Futurum and Oracle…" dataTestId="form-legal"/>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div>
                    <Label className="text-xs">Lead-capture form fields</Label>
                    <p className="text-[11px] text-slate-500 mt-0.5">Add, edit, reorder, or remove the fields shown on the public form.</p>
                  </div>
                  <Button size="sm" variant="outline" data-testid="form-field-add" onClick={() => {
                    const nf = { id: uidField(), name: "custom_field", label: "New field", type: "text", placeholder: "", required: false };
                    setDraft((d) => ({ ...d, form_fields_v2: [...(d.form_fields_v2 || []), nf] }));
                  }}>
                    <Plus className="w-3.5 h-3.5 mr-1"/> Add Field
                  </Button>
                </div>

                {(draft.form_fields_v2 || []).length === 0 ? (
                  <div className="rounded border border-dashed border-slate-300 p-3 text-[11px] text-slate-500">
                    No custom fields — the form will use the default set (Business Email, First/Last Name, Country, Company, Job Title).
                    Click <strong>Add Field</strong> to override.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(draft.form_fields_v2 || []).map((f, i) => (
                      <div key={f.id} className="rounded border border-slate-200 bg-white p-3" data-testid={`form-field-row-${i}`}>
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Label</Label>
                            <Input value={f.label || ""} onChange={(e) => {
                              const v = e.target.value;
                              setDraft((d) => ({ ...d, form_fields_v2: d.form_fields_v2.map((x) => x.id === f.id ? { ...x, label: v } : x) }));
                            }} className="h-8 text-sm" data-testid={`ff-label-${i}`}/>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Name (key)</Label>
                            <Input value={f.name || ""} onChange={(e) => {
                              const v = e.target.value.replace(/[^a-zA-Z0-9_]/g, "_");
                              setDraft((d) => ({ ...d, form_fields_v2: d.form_fields_v2.map((x) => x.id === f.id ? { ...x, name: v } : x) }));
                            }} className="h-8 text-sm" data-testid={`ff-name-${i}`}/>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Type</Label>
                            <Select value={f.type || "text"} onValueChange={(v) => {
                              setDraft((d) => ({ ...d, form_fields_v2: d.form_fields_v2.map((x) => x.id === f.id ? { ...x, type: v } : x) }));
                            }}>
                              <SelectTrigger className="h-8 text-sm" data-testid={`ff-type-${i}`}><SelectValue/></SelectTrigger>
                              <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Placeholder</Label>
                            <Input value={f.placeholder || ""} onChange={(e) => {
                              const v = e.target.value;
                              setDraft((d) => ({ ...d, form_fields_v2: d.form_fields_v2.map((x) => x.id === f.id ? { ...x, placeholder: v } : x) }));
                            }} className="h-8 text-sm" data-testid={`ff-ph-${i}`}/>
                          </div>
                          <div className="col-span-1 flex flex-col items-center pb-1">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Req.</Label>
                            <Switch checked={!!f.required} onCheckedChange={(v) => {
                              setDraft((d) => ({ ...d, form_fields_v2: d.form_fields_v2.map((x) => x.id === f.id ? { ...x, required: !!v } : x) }));
                            }} data-testid={`ff-req-${i}`}/>
                          </div>
                          <div className="col-span-1 flex flex-col items-end pb-1 gap-1">
                            <button type="button" title="Move up" disabled={i === 0} onClick={() => setDraft((d) => {
                              const arr = [...d.form_fields_v2]; if (i > 0) { [arr[i-1], arr[i]] = [arr[i], arr[i-1]]; } return { ...d, form_fields_v2: arr };
                            })} className="text-slate-400 hover:text-slate-700 text-xs disabled:opacity-30" data-testid={`ff-up-${i}`}>▲</button>
                            <button type="button" title="Move down" disabled={i >= (draft.form_fields_v2.length - 1)} onClick={() => setDraft((d) => {
                              const arr = [...d.form_fields_v2]; if (i < arr.length - 1) { [arr[i], arr[i+1]] = [arr[i+1], arr[i]]; } return { ...d, form_fields_v2: arr };
                            })} className="text-slate-400 hover:text-slate-700 text-xs disabled:opacity-30" data-testid={`ff-down-${i}`}>▼</button>
                          </div>
                        </div>

                        {/* Options row for dropdown type */}
                        {f.type === "select" && (
                          <div className="mt-2">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Options (one per line)</Label>
                            <Textarea rows={3} value={(f.options || []).join("\n")} onChange={(e) => {
                              const opts = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
                              setDraft((d) => ({ ...d, form_fields_v2: d.form_fields_v2.map((x) => x.id === f.id ? { ...x, options: opts } : x) }));
                            }} className="text-sm" data-testid={`ff-opts-${i}`}/>
                          </div>
                        )}

                        <div className="flex justify-end mt-1">
                          <button type="button" onClick={() => {
                            if (!window.confirm(`Delete field "${f.label}"?`)) return;
                            setDraft((d) => ({ ...d, form_fields_v2: d.form_fields_v2.filter((x) => x.id !== f.id) }));
                          }} className="text-rose-600 hover:text-rose-800 text-xs flex items-center gap-1" data-testid={`ff-del-${i}`}>
                            <Trash2 className="w-3.5 h-3.5"/> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Legacy quick-toggle grid — still available for pages that don't need custom fields */}
              <div>
                <Label className="text-xs">Default form fields (used if no custom fields above)</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {Object.entries({
                    full_name:      "Full name (First + Last)",
                    email:          "Business Email",
                    company:        "Company",
                    job_title:      "Job title",
                    phone:          "Phone",
                    country:        "Country dropdown",
                    marketing_opt:  "Marketing consent checkbox",
                    privacy_opt:    "Privacy policy checkbox",
                  }).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`form-field-${key}`}>
                      <Switch checked={!!draft.form_fields[key]} onCheckedChange={setFormField(key)}/>
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Consent statement editors — shown when the corresponding default checkbox is on */}
              {(draft.form_fields.marketing_opt || draft.form_fields.privacy_opt) && (
                <div className="rounded border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="consent-editor">
                  <Label className="text-xs font-semibold">Consent statements &amp; legal links</Label>
                  {draft.form_fields.marketing_opt && (
                    <div>
                      <Label className="text-[11px] text-slate-500">Marketing consent statement</Label>
                      <Textarea
                        rows={2}
                        value={draft.consent_config?.marketing_text || ""}
                        onChange={(e) => setConsent("marketing_text")(e.target.value)}
                        placeholder="Yes, I subscribe to marketing communications about products, events and services."
                        data-testid="consent-marketing-text"
                      />
                    </div>
                  )}
                  {draft.form_fields.privacy_opt && (
                    <>
                      <div>
                        <Label className="text-[11px] text-slate-500">Privacy consent statement <span className="text-slate-400">(the phrases "Privacy Policy" and "Terms of Use" become links automatically)</span></Label>
                        <Textarea
                          rows={2}
                          value={draft.consent_config?.privacy_text || ""}
                          onChange={(e) => setConsent("privacy_text")(e.target.value)}
                          placeholder="I agree to the Privacy Policy and Terms of Use."
                          data-testid="consent-privacy-text"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[11px] text-slate-500">Privacy Policy link</Label>
                          <Input
                            value={draft.consent_config?.privacy_url || ""}
                            onChange={(e) => setConsent("privacy_url")(e.target.value)}
                            placeholder="/privacy-policy/ (default)"
                            data-testid="consent-privacy-url"
                          />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Terms of Use link</Label>
                          <Input
                            value={draft.consent_config?.terms_url || ""}
                            onChange={(e) => setConsent("terms_url")(e.target.value)}
                            placeholder="/terms/ (default)"
                            data-testid="consent-terms-url"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">CTA label</Label>
                  <Input value={draft.cta_label} onChange={(e) => setF("cta_label")(e.target.value)} data-testid="form-cta"/>
                </div>
                <div>
                  <Label className="text-xs">PDF download URL</Label>
                  <Input value={draft.pdf_url} onChange={(e) => setF("pdf_url")(e.target.value)} placeholder="https://…/whitepaper.pdf" data-testid="form-pdf"/>
                  <PdfUploader onUploaded={setF("pdf_url")}/>
                </div>
              </div>
                </TabsContent>

                <TabsContent value="builder" className="flex-1 min-h-0 overflow-hidden p-3 m-0">
                  <LandingPageBuilder
                    blocks={draft.blocks || []}
                    onChange={(nb) => setDraft((d) => ({ ...d, blocks: typeof nb === "function" ? nb(d.blocks || []) : nb }))}
                  />
                </TabsContent>
              </Tabs>
            </div>

            {/* Right — Live Preview */}
            <div className="overflow-y-auto bg-slate-100 p-6" data-testid="landing-editor-preview">
              <LivePreview page={draft}/>
              {draft.id && draft.status === "published" && (
                <a
                  href={previewUrl(draft)}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:underline mt-4"
                  data-testid="landing-view-live"
                >
                  <ExternalLink className="w-3.5 h-3.5"/> View live page
                </a>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * LivePreview — renders the landing-page draft in the same visual
 * language as the public detail page so the admin sees exactly what
 * a visitor will get.
 */
function LivePreview({ page }) {
  const paragraphs = (page.abstract || "").split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  const blocks = Array.isArray(page.blocks) ? page.blocks : [];
  return (
    <div className="max-w-[720px] mx-auto bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-8 space-y-5">
        <h1 className="text-2xl md:text-3xl font-extrabold uppercase text-slate-900 leading-tight tracking-tight">
          {page.title || "Your whitepaper title will appear here."}
        </h1>
        {page.cover_url ? (
          <img src={page.cover_url} alt="" className="w-full rounded border border-slate-200 max-h-64 object-cover"/>
        ) : (
          <div className="w-full h-40 rounded border border-dashed border-slate-300 grid place-items-center text-slate-400 text-xs">Cover image preview</div>
        )}

        {/* Builder blocks (Phase 2) — render inline if present */}
        {blocks.length > 0 && (
          <div className="space-y-3">
            {blocks.map((b) => <PreviewBlock key={b.id} block={b}/>)}
          </div>
        )}

        <div className="space-y-3 text-slate-700 text-sm leading-relaxed">
          {paragraphs.length === 0 ? (
            <p className="text-slate-400">Abstract / body paragraphs will render here.</p>
          ) : paragraphs.map((p, i) => <p key={i}><RichText text={p}/></p>)}
        </div>
        {page.highlights.length > 0 && (
          <div>
            <div className="text-xs font-bold text-cyan-700 uppercase tracking-wider mb-2">Highlights</div>
            <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
              {page.highlights.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          </div>
        )}
        {page.legal_blurb && (
          <p className="text-xs italic text-slate-500 border-l-2 border-slate-200 pl-3">
            <RichText text={page.legal_blurb}/>
          </p>
        )}

        {/* Lead form preview */}
        <div className="rounded border border-slate-200 bg-slate-50 p-4 space-y-2.5">
          <div className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Get your free copy
          </div>
          <div className="grid grid-cols-2 gap-2">
            {page.form_fields.full_name && <MockInput label="Full name*"/>}
            {page.form_fields.email && <MockInput label="Work email*"/>}
            {page.form_fields.company && <MockInput label="Company*"/>}
            {page.form_fields.job_title && <MockInput label="Job title"/>}
            {page.form_fields.phone && <MockInput label="Phone"/>}
            {page.form_fields.country && <MockSelect label="Country"/>}
          </div>
          {page.form_fields.marketing_opt && (
            <div className="flex items-start gap-2 text-[11px] text-slate-600">
              <span className="mt-0.5 w-3.5 h-3.5 rounded border border-slate-400 shrink-0"/>
              <span data-testid="preview-marketing-label">{(page.consent_config?.marketing_text || "").trim() || "Yes, I subscribe to marketing communications about products, events and services."}</span>
            </div>
          )}
          {page.form_fields.privacy_opt && (
            <div className="flex items-start gap-2 text-[11px] text-slate-600">
              <span className="mt-0.5 w-3.5 h-3.5 rounded border border-slate-400 shrink-0"/>
              <span data-testid="preview-privacy-label"><LegalText text={(page.consent_config?.privacy_text || "").trim() || "I agree to the Privacy Policy and Terms of Use."} cc={page.consent_config || {}}/></span>
            </div>
          )}
          <button type="button" className="w-full mt-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded px-4 py-2 text-sm font-semibold" data-testid="preview-cta">
            {page.cta_label || "Download Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
function LegalText({ text, cc }) {
  const pUrl = (cc.privacy_url || "").trim() || "/sbt-site/privacy-policy/";
  const tUrl = (cc.terms_url || "").trim() || "/sbt-site/terms/";
  const parts = String(text).split(/(Privacy Policy|Terms of (?:Use|Service))/gi);
  return parts.map((part, i) => {
    if (/^Privacy Policy$/i.test(part)) return <a key={i} href={pUrl} target="_blank" rel="noopener noreferrer" className="underline text-cyan-700">{part}</a>;
    if (/^Terms of (Use|Service)$/i.test(part)) return <a key={i} href={tUrl} target="_blank" rel="noopener noreferrer" className="underline text-cyan-700">{part}</a>;
    return part;
  });
}
function MockInput({ label }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 mb-0.5">{label}</div>
      <div className="w-full h-8 rounded bg-white border border-slate-200"/>
    </div>
  );
}
function MockSelect({ label }) {
  return (
    <div className="col-span-2">
      <div className="text-[10px] text-slate-500 mb-0.5">{label}</div>
      <div className="w-full h-8 rounded bg-white border border-slate-200 grid place-items-end pr-2 text-[10px] text-slate-400">▾</div>
    </div>
  );
}

/**
 * PreviewBlock — small approximation of each block type for the
 * admin live-preview panel. Deliberately compact; the public detail
 * page uses a fuller renderer.
 */
function PreviewBlock({ block }) {
  const p = block.props || {};
  switch (block.type) {
    case "title": {
      const Tag = `h${Math.max(1, Math.min(4, p.level || 2))}`;
      return <Tag className="font-bold text-slate-900" data-testid={`preview-blk-${block.type}`}>{p.text || ""}</Tag>;
    }
    case "text": return <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid={`preview-blk-${block.type}`}>{p.html || ""}</p>;
    case "image": return p.src ? <img src={p.src} alt={p.alt || ""} style={{ width: `${p.width_pct || 100}%` }} className="rounded border border-slate-200"/> : null;
    case "youtube": {
      const id = extractYouTubeId(p.url || "");
      return id
        ? <iframe title="YouTube video" className="w-full aspect-video rounded" src={`https://www.youtube.com/embed/${id}`} allowFullScreen/>
        : <div className="text-xs text-slate-400">YouTube URL missing.</div>;
    }
    case "email":
    case "phone":
    case "number":
    case "full_name":
    case "text_short":
    case "inline":
    case "lookup":
    case "address":
      return (
        <label className="block">
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          <input type={block.type === "email" ? "email" : block.type === "phone" ? "tel" : block.type === "number" ? "number" : "text"} placeholder={p.placeholder || ""} className="w-full h-8 rounded border border-slate-300 px-2 text-sm"/>
        </label>
      );
    case "text_long":
      return (
        <label className="block">
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          <textarea rows={p.rows || 3} placeholder={p.placeholder || ""} className="w-full rounded border border-slate-300 px-2 py-1 text-sm"/>
        </label>
      );
    case "dropdown":
      return (
        <label className="block">
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          <select className="w-full h-8 rounded border border-slate-300 px-2 text-sm">
            <option value="">Select…</option>
            {(p.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
          </select>
        </label>
      );
    case "single":
      return (
        <div>
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          {(p.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm py-0.5"><input type="radio" name={`s_${block.id}`}/><span>{o}</span></label>)}
        </div>
      );
    case "multi":
      return (
        <div>
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          {(p.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm py-0.5"><input type="checkbox"/><span>{o}</span></label>)}
        </div>
      );
    case "switch":
      return <label className="flex items-center gap-2 text-sm"><input type="checkbox" defaultChecked={!!p.default}/><span>{p.label}</span></label>;
    case "range":
      return (
        <label className="block">
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}</div>
          <input type="range" min={p.min} max={p.max} step={p.step} defaultValue={p.default} className="w-full"/>
        </label>
      );
    case "date": case "time": case "datetime":
      return (
        <label className="block">
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          <input type={block.type === "datetime" ? "datetime-local" : block.type} className="h-8 rounded border border-slate-300 px-2 text-sm"/>
        </label>
      );
    case "rating":
      return (
        <div>
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}</div>
          <div className="text-yellow-400 text-lg">{"★".repeat(p.max || 5)}</div>
        </div>
      );
    case "signature":
      return (
        <div>
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          <div className="h-16 rounded border border-dashed border-slate-300 grid place-items-center text-xs text-slate-400">Sign here</div>
        </div>
      );
    case "file_upload":
      return (
        <label className="block">
          <div className="text-[11px] font-bold text-slate-600 mb-0.5">{p.label}{p.required ? " *" : ""}</div>
          <input type="file" accept={p.accept || undefined} className="text-xs"/>
        </label>
      );
    case "terms":
      return <label className="flex items-start gap-2 text-xs"><input type="checkbox" className="mt-0.5"/><span>{p.label}</span></label>;
    case "captcha":
      return <div className="text-xs text-slate-400 border border-dashed rounded p-2 text-center">Google Captcha (site key: {p.site_key || "not set"})</div>;
    case "table":
      return (
        <div className="text-xs">
          <div className="font-bold mb-1">{p.label}</div>
          <table className="w-full border">
            <thead><tr>{(p.columns || []).map((c, i) => <th key={i} className="border px-1 py-0.5 bg-slate-50">{c}</th>)}</tr></thead>
            <tbody><tr>{(p.columns || []).map((_, i) => <td key={i} className="border px-1 py-0.5">&nbsp;</td>)}</tr></tbody>
          </table>
        </div>
      );
    case "calculation":
      return <div className="text-xs text-slate-500 italic">Σ {p.label}: <code>{p.formula || "—"}</code></div>;
    default:
      return null;
  }
}

function extractYouTubeId(url) {
  if (!url) return "";
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,15})/);
  return m ? m[1] : "";
}
